/**
 * The alert queue's pure half — bata-ui#166 W3-3/W3-4, bata-platform ADR 2102/2103, `docs/adr/2102`.
 *
 * The platform folds each alert's append-only event log into a state (`open`, `snoozed`,
 * `decided`), a due date (raised + SLA, never moved by a snooze) and `overdue`; this module only
 * reads that answer and composes the acts a planner takes on it:
 *
 *   GET  /foundations/exceptions?queue=mine|claimable|all&state=&overdue=
 *   POST /foundations/exceptions/{id}/assignment   own it without deciding (DEC-13)
 *   POST /foundations/exceptions/{id}/snooze       out of the queue until a date
 *   POST /foundations/exceptions/{id}/reopen       withdraw a decision, appended
 *   GET  /foundations/exceptions/{id}/history      the log
 *
 * **The URL is the scope** (CLAUDE.md §3.4): `?queue=`, `?state=` and `?overdue=` are parsed from
 * the search string here, so a planner's "my overdue alerts" is a link. Unlike a run id these are
 * filters over a read that is already bounded by the caller's grant, so each has a default — the
 * one the queue is for: `mine`, `open`.
 *
 * **Reasons are the platform's.** A snooze carries a code whose `acts` include `snooze`, a reopen
 * one whose `acts` include `reopen`. The reopen codes are the owner's ratified set (R-1), live only
 * once the registry row naming them is approved (bata-platform ADR 2104); until then the platform
 * publishes none and the reopen control renders that refusal rather than inventing a code.
 */

import type {
  AlertReasonOption, AlertState, AlertSummary, RecordAlertAssignmentParams, RecordAlertReopenParams,
  RecordAlertSnoozeParams,
} from '@/api/generated';

import type { AlertSummaryWithName } from '@/api/pending/alert-groups';
import { personAbsentReason, type PersonAbsentReason } from '@/planning';

import { COUNTRY_PARAM, readCountry } from './data-schedules';

export type AlertQueueName = 'mine' | 'claimable' | 'unassigned' | 'all';

/** `unassigned` is every alert with no assignee, whatever its state or rule (platform ADR 6194); `claimable` is a subset of it. */
export const ALERT_QUEUES: readonly AlertQueueName[] = ['mine', 'claimable', 'unassigned', 'all'];
export const ALERT_STATES: readonly AlertState[] = ['open', 'snoozed', 'decided'];

export const QUEUE_PARAM = 'queue';
export const STATE_PARAM = 'state';
export const OVERDUE_PARAM = 'overdue';

/** Each queue's label — spelled out so the catalogue check sees every key. */
export const QUEUE_LABEL_KEY: Readonly<Record<AlertQueueName, string>> = {
  mine: 'foundations.alerts.queue.mine',
  claimable: 'foundations.alerts.queue.claimable',
  unassigned: 'foundations.alerts.queue.unassigned',
  all: 'foundations.alerts.queue.all',
};

/** Each state's label. */
export const STATE_LABEL_KEY: Readonly<Record<AlertState, string>> = {
  open: 'foundations.alerts.state.open',
  snoozed: 'foundations.alerts.state.snoozed',
  decided: 'foundations.alerts.state.decided',
};

export interface AlertQueueScope {
  readonly queue: AlertQueueName;
  readonly state: AlertState;
  /** Only overdue alerts — `?overdue=true`. */
  readonly overdueOnly: boolean;
}

function isQueue(value: string | null): value is AlertQueueName {
  return value !== null && (ALERT_QUEUES as readonly string[]).includes(value);
}

function isState(value: string | null): value is AlertState {
  return value !== null && (ALERT_STATES as readonly string[]).includes(value);
}

/** The queue a link names. Pure: a string in, never `window`. */
export function readAlertQueueScope(search: string): AlertQueueScope {
  const params = new URLSearchParams(search);
  const queue = params.get(QUEUE_PARAM);
  const state = params.get(STATE_PARAM);
  return {
    queue: isQueue(queue) ? queue : 'mine',
    state: isState(state) ? state : 'open',
    overdueOnly: params.get(OVERDUE_PARAM) === 'true',
  };
}

/** The search string for a scope, keeping every other parameter the link carries. */
export function writeAlertQueueScope(search: string, scope: AlertQueueScope): string {
  const params = new URLSearchParams(search);
  params.set(QUEUE_PARAM, scope.queue);
  params.set(STATE_PARAM, scope.state);
  if (scope.overdueOnly) params.set(OVERDUE_PARAM, 'true');
  else params.delete(OVERDUE_PARAM);
  const text = params.toString();
  return text === '' ? '' : `?${text}`;
}

/**
 * `?department=` — the department family the queue is narrowed to (owner ruling R-12,
 * bata-platform ADR 2106, `docs/adr/2105`).
 *
 * The family is derived by the platform at read, from the market's pinned product class
 * scheme — the in-season drill's own grouping — so it is a question about ONE market: the
 * department filter travels with `?country=`. **No default for either.** No market is every
 * market in the caller's scope; no department is every department. A department on a link
 * with no market is still sent, and the platform's named refusal
 * (`alert_department_needs_market`) is what the screen shows: guessing the market would be
 * answering a different question from the one the link asks.
 */
export const DEPARTMENT_PARAM = 'department';
export { COUNTRY_PARAM };

/** The "no narrowing" option of the market and department pickers (Radix forbids `''`). */
export const ALL_OPTION = '__all__';

export interface AlertDepartmentScope {
  /** `?country=` — one market, or `null` for every market in scope. */
  readonly country: string | null;
  /** `?department=` — one family of that market's scheme, or `null`. */
  readonly department: string | null;
}

/** The market and department a link names. Pure: a string in, never `window`. */
export function readAlertDepartmentScope(search: string): AlertDepartmentScope {
  const raw = new URLSearchParams(search).get(DEPARTMENT_PARAM)?.trim() ?? '';
  return { country: readCountry(search), department: raw === '' ? null : raw };
}

/**
 * The single alerts' subject filters (usability audit C5, 2026-10-02): `?rule=`, `?store=` and
 * `?article=` — the grouped inbox's own keys, so switching view keeps them — sent as the read's
 * exact `rule_id`, `store_key`, `article_key` (`-`: the alerts with none, the platform's own
 * reading). `?owner=` is matched over the rows read: the read's `assignee` is deprecated and reads
 * "assigned to X OR unassigned" (bata-platform `list_exceptions`), which is not "owned by X".
 */
export const SUBJECT_RULE_PARAM = 'rule';
export const SUBJECT_STORE_PARAM = 'store';
export const SUBJECT_ARTICLE_PARAM = 'article';
export const OWNER_PARAM = 'owner';

export interface AlertSubjectScope {
  readonly rule: string | null;
  readonly store: string | null;
  readonly article: string | null;
  /** An assignee id, matched over the rows read. */
  readonly owner: string | null;
}

export const NO_SUBJECT: AlertSubjectScope = { rule: null, store: null, article: null, owner: null };

/** The subject filters a link names. Pure. */
export function readAlertSubjectScope(search: string): AlertSubjectScope {
  const params = new URLSearchParams(search);
  const read = (key: string): string | null => {
    const raw = params.get(key)?.trim() ?? '';
    return raw === '' ? null : raw;
  };
  return {
    rule: read(SUBJECT_RULE_PARAM), store: read(SUBJECT_STORE_PARAM), article: read(SUBJECT_ARTICLE_PARAM), owner: read(OWNER_PARAM),
  };
}

/** The alerts the owner filter keeps — every alert when it is unset. Pure. */
export function alertsOwnedBy<T extends Pick<AlertSummary, 'assignee'>>(alerts: readonly T[], owner: string | null): readonly T[] {
  return owner === null ? alerts : alerts.filter((alert) => ownerOf(alert) === owner);
}

/**
 * The list's filters as the export's provenance states them: the queue, state and overdue, the
 * market and department, and the subject filters — every narrowing of the file, in the read's
 * own names (`owner` is the screen's: it is matched over the rows). Pure.
 */
export function alertListProvenance(
  scope: AlertQueueScope, department: AlertDepartmentScope, subject: AlertSubjectScope,
): readonly { readonly field: string; readonly operator: string; readonly value: string }[] {
  const equals = (field: string, value: string | null) => (value === null ? [] : [{ field, operator: 'equals', value }]);
  return [
    ...equals('queue', scope.queue),
    ...equals('state', scope.state),
    ...(scope.overdueOnly ? equals('overdue', 'true') : []),
    ...equals('country_key', department.country),
    ...equals('department_family', department.department),
    ...equals('rule_id', subject.rule),
    ...equals('store_key', subject.store),
    ...equals('article_key', subject.article),
    ...equals('owner', subject.owner),
  ];
}

/** Whether the link asks for a department with no market — the platform refuses it (422). */
export function departmentNeedsMarket(scope: AlertDepartmentScope): boolean {
  return scope.department !== null && scope.country === null;
}

/**
 * The read's query for a scope. `open_only` is ignored by the platform once `state` is given.
 * `country_key` and `department_family` are sent exactly as the link names them.
 */
export function alertQueueQuery(
  scope: AlertQueueScope,
  department: AlertDepartmentScope = { country: null, department: null },
  subject: AlertSubjectScope = NO_SUBJECT,
): {
  readonly queue: AlertQueueName;
  readonly state: AlertState;
  readonly open_only: boolean;
  readonly overdue?: boolean;
  readonly country_key?: string;
  readonly department_family?: string;
  readonly rule_id?: string;
  readonly store_key?: string;
  readonly article_key?: string;
} {
  return {
    queue: scope.queue,
    state: scope.state,
    open_only: false,
    ...(scope.overdueOnly ? { overdue: true } : {}),
    ...(department.country === null ? {} : { country_key: department.country }),
    ...(department.department === null ? {} : { department_family: department.department }),
    ...(subject.rule === null ? {} : { rule_id: subject.rule }),
    ...(subject.store === null ? {} : { store_key: subject.store }),
    ...(subject.article === null ? {} : { article_key: subject.article }),
  };
}

/**
 * Rows the per-alert list reads: one page of the queue, most severe first (`order=severity_oldest`, oldest
 * first within a severity, bata-platform ADR 2130), with `total` beside it. The queue is never read
 * whole — about 191,000 open alerts in production — so the order is the store's, and the figures are
 * the platform's counts, not sums over a page.
 */
export const ALERT_LIST_PAGE = 200;

/** The list's read: the queue's query, most severe first, one page. */
export function alertListPageQuery(base: ReturnType<typeof alertQueueQuery>): ReturnType<typeof alertQueueQuery> & {
  readonly order: 'severity_oldest'; readonly limit: number;
} {
  return { ...base, order: 'severity_oldest', limit: ALERT_LIST_PAGE };
}

/** The reasons an act may carry, in the platform's order. */
export function reasonsForAct(options: readonly AlertReasonOption[], act: 'snooze' | 'reopen'): readonly AlertReasonOption[] {
  return options.filter((option) => (option.acts ?? []).includes(act));
}

/** Take ownership (`assignee` = me) or release (`null`). `null` while nobody is signed in. */
export function composeAssignment(alertId: string, assignee: string | null, commentary = ''): RecordAlertAssignmentParams | null {
  if (alertId.trim() === '') return null;
  const note = commentary.trim();
  return {
    path: { alert_id: alertId },
    body: { assignee, ...(note === '' ? {} : { commentary: note }) },
  };
}

/** A snooze until a calendar day, or `null` while the day or the reason is missing. */
export function composeSnooze(
  alertId: string, until: string | null, reasonCode: string | null, commentary = '',
): RecordAlertSnoozeParams | null {
  if (alertId.trim() === '' || until === null || !/^\d{4}-\d{2}-\d{2}$/.test(until)) return null;
  if (reasonCode === null || reasonCode.trim() === '') return null;
  const note = commentary.trim();
  return {
    path: { alert_id: alertId },
    body: { until, reasonCode, ...(note === '' ? {} : { commentary: note }) },
  };
}

/** A reopen with its reason, or `null` while the reason is missing. */
export function composeReopen(alertId: string, reasonCode: string | null, commentary = ''): RecordAlertReopenParams | null {
  if (alertId.trim() === '' || reasonCode === null || reasonCode.trim() === '') return null;
  const note = commentary.trim();
  return {
    path: { alert_id: alertId },
    body: { reasonCode, ...(note === '' ? {} : { commentary: note }) },
  };
}

/** The first day a snooze may name: tomorrow, in UTC — the platform judges on its own clock. */
export function earliestSnoozeDay(now: number): string {
  const tomorrow = new Date(now + 24 * 60 * 60 * 1000);
  return tomorrow.toISOString().slice(0, 10);
}

/** Whether `until` is after today (UTC). The platform refuses otherwise; this only guides. */
export function snoozeDayIsFuture(until: string, now: number): boolean {
  return until >= earliestSnoozeDay(now);
}

/** Who owns the alert, as the queue states it — the assignee, else nobody. */
export function ownerOf(alert: Pick<AlertSummary, 'assignee'>): string | null {
  const owner = alert.assignee ?? null;
  return owner === null || owner.trim() === '' ? null : owner;
}

/**
 * Who owns the alert as a planner reads it (DEC-102): their name when the platform sends one
 * (`assigneeName`, from the profile), else — where the read says why there is none and the caller
 * gives `reasonWords` — that reason in words with the `id` to put behind the «id» button, else their
 * id — `mono` — else `null` for nobody.
 */
export function ownerName(
  alert: Pick<AlertSummaryWithName, 'assignee' | 'assigneeName' | 'assigneeNameAbsentReason'>,
  reasonWords?: (reason: PersonAbsentReason) => string,
): { readonly text: string; readonly mono: boolean; readonly id?: string } | null {
  const id = ownerOf(alert);
  if (id === null) return null;
  const name = alert.assigneeName ?? null;
  if (name !== null && name.trim() !== '') return { text: name, mono: false };
  // ADR 2127: the read says why there is no name — said in words, the id behind the «id» button.
  const reason = personAbsentReason(alert.assigneeNameAbsentReason);
  if (reason !== null && reasonWords !== undefined) return { text: reasonWords(reason), mono: false, id };
  return { text: id, mono: true };
}

/** Whether the alert is the signed-in person's. */
export function isMine(alert: Pick<AlertSummary, 'assignee'>, subject: string | null): boolean {
  return subject !== null && ownerOf(alert) === subject;
}
